import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { printerService } from '../../../services/printerService';

const MaintenanceSchedulePanel = ({ printerStatus, loading, onSchedule }) => {
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleNotes, setScheduleNotes] = useState('');

  useEffect(() => {
    loadMaintenanceHistory();
  }, []);

  const loadMaintenanceHistory = async () => {
    try {
      const history = await printerService?.getMaintenanceHistory();
      setMaintenanceHistory(history);
    } catch (error) {
      console.error('Failed to load maintenance history:', error);
    }
  };

  const handleScheduleMaintenance = async () => {
    try {
      await printerService?.scheduleMaintenance(scheduleDate, scheduleNotes);
      alert('Maintenance scheduled successfully');
      setShowScheduleModal(false);
      setScheduleDate('');
      setScheduleNotes('');
      onSchedule();
    } catch (error) {
      alert('Failed to schedule maintenance');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading maintenance data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Maintenance Schedule</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track printer maintenance and schedule cleaning cycles
          </p>
        </div>
        <button
          onClick={() => setShowScheduleModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Icon name="Plus" className="w-4 h-4" />
          Schedule Maintenance
        </button>
      </div>
      {/* Maintenance Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Last Maintenance</h3>
              <p className="text-sm text-gray-600">
                {printerStatus?.lastMaintenance 
                  ? new Date(printerStatus.lastMaintenance)?.toLocaleDateString()
                  : 'N/A'
                }
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Routine cleaning and ribbon replacement completed
          </p>
        </div>

        <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Next Maintenance</h3>
              <p className="text-sm text-gray-600">
                {printerStatus?.nextMaintenance 
                  ? new Date(printerStatus.nextMaintenance)?.toLocaleDateString()
                  : 'Not scheduled'
                }
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Scheduled cleaning cycle and inspection
          </p>
        </div>
      </div>
      {/* Maintenance Checklist */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Maintenance Checklist</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckSquare" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Clean Print Head</h4>
              <p className="text-sm text-gray-600">Remove dust and debris from print head</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckSquare" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Replace Ribbon</h4>
              <p className="text-sm text-gray-600">Install new YMCKO ribbon cartridge</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckSquare" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Clean Rollers</h4>
              <p className="text-sm text-gray-600">Clean card transport rollers</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckSquare" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Inspect Hardware</h4>
              <p className="text-sm text-gray-600">Check for wear and damage</p>
            </div>
          </div>
        </div>
      </div>
      {/* Maintenance History */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Maintenance History</h3>
        {maintenanceHistory?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Icon name="Inbox" className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p>No maintenance history available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {maintenanceHistory?.map((record) => (
              <div 
                key={record?.id}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Icon name="Wrench" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">{record?.type}</h4>
                      <p className="text-sm text-gray-600 mt-1">{record?.notes}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Performed by {record?.performedBy} on {new Date(record.date)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Schedule Maintenance</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maintenance Date
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={scheduleNotes}
                  onChange={(e) => setScheduleNotes(e?.target?.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add maintenance notes..."
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleMaintenance}
                disabled={!scheduleDate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceSchedulePanel;
